import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import client from '../../api/client';

/**
 * Renders an authenticated receipt preview (image or PDF).
 * For SERVER paths it uses apiClient with JWT to fetch the file as a blob, then
 * creates a local blob: URL for the <img> or <iframe> tag (cleaned up on unmount).
 *
 * For LOCAL sources — a just-selected file's blob:/data: URL — it uses the src
 * DIRECTLY, with no XHR. A blob: URL needs no auth, and the CSP blocks fetching
 * blob: via connect-src (an XHR would be (blocked:csp)), whereas img-src allows
 * blob: — so the direct path both works and avoids a pointless round-trip. This
 * mirrors the upload thumbnail, which already renders local blobs as a raw <img>.
 */
export default function AuthedReceiptPreview({ src, fileType, alt, style, imgStyle, iframeStyle }) {
  const isLocal = !!src && (src.startsWith('blob:') || src.startsWith('data:'));

  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    // Local blob:/data: sources are used directly — never fetched.
    if (!src || isLocal) { setLoading(false); return; }
    let cancelled = false;
    let objectUrl = null;
    setLoading(true);
    setError(false);
    setBlobUrl(null);
    client.get(src, { responseType: 'blob' })
      .then(res => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setBlobUrl(objectUrl);
      })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    // Revoke the URL created by THIS effect run (not the state value, which
    // would be stale here) — only on unmount or when src changes, never while
    // it is still bound to the live <img>/<iframe>.
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src, isLocal]);

  // Server sources show a spinner while fetching; local sources render immediately.
  if (!isLocal && loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', ...style }}>
        <Loader2 size={16} className="spin" style={{ color: '#94a3b8' }} />
      </div>
    );
  }

  const displayUrl = isLocal ? src : blobUrl;
  if (error || !displayUrl) return null;

  const isImage = fileType?.startsWith('image/');
  const isPdf = fileType === 'application/pdf';

  if (isImage) {
    return <img src={displayUrl} alt={alt || 'recibo'} style={imgStyle || style} />;
  }

  if (isPdf) {
    return <iframe src={displayUrl} title={alt || 'recibo'} style={iframeStyle || style} />;
  }

  return null;
}
